import React from 'react';
import { useNavigate } from 'react-router-dom';
import { isEqual, isNil, isUndefined } from 'lodash-es';
import styled from 'styled-components';

import PageLoader from '@/components/page-loader';
import { DataElementContext } from '@/page-components/common/DataElementContext';
import { useAppDispatch, useAppSelector } from '@/store';
import { authenticationLoginEmail, updateUser } from '@/store/slices/authentication';
import { sessionEnsure } from '@/store/slices/session';
import { isEmailValid } from '@/utils/strings';
import { resolvePostAuthRoute } from '@/utils/post-auth-route';
import { resolveConfiguratorFlowBase } from '@/utils/configurator-flow';

interface RegisterProps {
  children: React.ReactNode;
  id: string;
  nodeId: string;
  parentNodeId: string;
  refId: string;
  refType: string;
  uniqueId: string;
  styleText: string;
  className: string;
  properties?: Record<string, string>;
}

const ModuleElementDiv = styled.div<{ $styleText: string }>(
  (props) => `
    ${props.$styleText};

    /* The app sets \`color-scheme: dark\` globally (src/index.scss) so browser-painted
       surfaces stay dark. That also flips the UA's default form-control text color to
       white, which turns the config-styled white email field white-on-white. Force the
       light UA scheme on the text inputs so their default text is dark again; any
       explicit color a tenant's config sets still wins. Checkboxes/radios are left on
       the dark scheme. */
    & input:not([type='checkbox']):not([type='radio']) {
      color-scheme: light;
    }
  `,
);

const RegisterModule: React.FC<RegisterProps> = (componentProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const authentication = useAppSelector((state) => state.authentication, isEqual);
  const casinoInstance = useAppSelector((state) => state.casinoInstance, isEqual);
  const session = useAppSelector((state) => state.session, isEqual);

  const [layout, setLayout] = React.useState<'form' | 'next'>('form');
  const [email, setEmail] = React.useState<string | undefined>();
  const [termsAgreed, setTermsAgreed] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);

  // Init session: resume an existing build or create one (new-owner funnel).
  React.useEffect(() => {
    dispatch(sessionEnsure());
  }, [dispatch]);

  // Post authentication handler
  React.useEffect(() => {
    // Unified with login + the root guard via the shared resolver — new users
    // (no brand) now go to configurator-v3, not the legacy v2 configurator.
    const nextPage = resolvePostAuthRoute({
      sessionStatus: session.status,
      brandsLoaded: casinoInstance.loaded,
      brandsCount: casinoInstance.brands.length,
      configuratorBase: resolveConfiguratorFlowBase(),
    });

    switch (session.postAuth) {
      case 'mail':
        setLayout('next');
        break;

      case 'redirect':
        setLoading(true);
        navigate(nextPage);
        break;

      default:
        break;
    }
  }, [
    navigate,
    session.postAuth,
    session.status,
    casinoInstance.brands.length,
    casinoInstance.loaded,
    casinoInstance.loading,
  ]);

  // Email input change handler
  const handleChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>((event) => {
    setEmail(event.target.value);
  }, []);

  // Terms input change handler
  const handleCheck = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>((event) => {
    setTermsAgreed((prevState) => !prevState);
  }, []);

  // Form validation
  const isValid = React.useMemo<boolean>(() => {
    return isEmailValid(email);
  }, [email]);

  // Form submitting clicking button
  const handleSubmit = React.useCallback<React.FormEventHandler<HTMLFormElement>>(
    (event) => {
      event.preventDefault();

      if (isUndefined(email) || isNil(session.sessionId) || !isValid) {
        return;
      }

      dispatch(updateUser({ email }));

      dispatch(authenticationLoginEmail({ email, sessionId: session.sessionId }));
    },
    [dispatch, session, email, isValid],
  );

  // Form submitting pressing enter key
  const handleKeyDown = React.useCallback<React.KeyboardEventHandler<HTMLFormElement>>(
    (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();

        handleSubmit(event);
      }
    },
    [handleSubmit],
  );

  // Final context
  const contextValue = React.useMemo(
    () => ({
      layout,
      email,
      checked: termsAgreed,
      isValid,
      onChange: handleChange,
      onKeyDown: handleKeyDown,
      onCheck: handleCheck,
      onSubmit: handleSubmit,
    }),
    [layout, email, termsAgreed, isValid, handleChange, handleKeyDown, handleCheck, handleSubmit],
  );

  if (loading) {
    return <PageLoader />;
  }

  return (
    <ModuleElementDiv className={componentProps.className} $styleText={componentProps.styleText}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default RegisterModule;
